import { BADGE_COLOR_STYLE, NEUTRAL_BADGE_STYLE } from "../constants";
import type { BadgeColor } from "../types";

interface Props {
  value: string;
  options: string[];
  optionColors?: Record<string, BadgeColor>;
  onChange: (next: string) => void;
  placeholder: string;
}

const CARET_BACKGROUND = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 6px center",
} as const;

export function styleForOption(
  value: string,
  optionColors?: Record<string, BadgeColor>,
) {
  const color = optionColors?.[value];
  return color ? BADGE_COLOR_STYLE[color] : NEUTRAL_BADGE_STYLE;
}

// Colored counterpart to StatusBadge for `badge` columns, where the option
// list and its colors are user-defined rather than the standard six statuses.
export function BadgeSelect({
  value,
  options,
  optionColors,
  onChange,
  placeholder,
}: Props) {
  const s = styleForOption(value, optionColors);

  // An unset cell shows the placeholder as plain text rather than a pill, so a
  // column of blanks doesn't read as a wall of gray chips.
  if (!value) {
    return (
      <select
        value=""
        onChange={(e) => onChange(e.target.value)}
        className="editable px-1 py-0.5 text-xs text-slate-400 bg-transparent cursor-pointer w-full"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`inline-flex items-center rounded-full font-medium cursor-pointer appearance-none pr-5 pl-5 py-0.5 text-xs ${s.badge} status-select`}
        style={CARET_BACKGROUND}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        {!options.includes(value) && <option value={value}>{value}</option>}
      </select>
      <span
        className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full shrink-0 pointer-events-none ${s.dot}`}
      />
    </div>
  );
}
